<template>
  <div>
    <v-card-title class="mt-3" style="justify-content: space-between">
      <h1 class="ml-2">Sprawozdanie końcowe</h1>
      <div style="display: flex">
        <v-tooltip
          top
          v-if="canCancelCorrections && reportMetadata.correctionCause"
        >
          <template v-slot:activator="{ on }">
            <div class="mr-2">
              <v-icon color="white" small v-on="on" style="padding-right: 10px"
                >info</v-icon
              >
            </div>
          </template>
          <div>
            <b>Sprawozdanie oczekuje na poprawki</b>
          </div>
          <span>{{ reportMetadata.correctionCause }}</span>
        </v-tooltip>
        <h2 class="mr-2">Status: {{ reportStatusMessage }}</h2>
      </div>
    </v-card-title>
    <div style="margin-left: 10px" v-if="loaded && reportGenerated">
      <v-btn v-if="!reportVisible" @click="showReport">
        Zobacz podgląd sprawozdania
      </v-btn>

      <v-btn v-if="reportVisible" @click="hideReport">
        Ukryj podgląd sprawozdania
      </v-btn>

      <v-btn v-if="canSeeComparison" @click="showReportComparison">
        Zobacz porównanie zmian
      </v-btn>

      <v-btn v-if="canApprove" color="green" @click="openApprovalDialog">
        Zatwierdź sprawozdanie
      </v-btn>

      <v-btn
        v-if="
          canUndoApproval &&
          (hasProjectPanelSupervisorModificationAccess())
        "
        @click="undoApproval"
        class="error"
      >
        Cofnij zatwierdzenie sprawozdania
      </v-btn>

      <v-btn v-if="canCorrect" color="orange" @click="openCorrectDialog">
        Oddaj sprawozdanie do poprawek
      </v-btn>

      <v-btn v-if="canCancelCorrections" color="red" @click="cancelCorrections">
        Cofnij oddanie do poprawek
      </v-btn>

      <v-btn v-if="canViewFinancialDocuments" @click="viewFinancialDocuments()">
        Dokumenty finansowe
      </v-btn>

      <v-btn v-if="exportAvailable" @click="exportProjectCosts">
        Eksportuj tabelę kosztów
      </v-btn>

      <v-btn class="no-print" @click="openReportView">
        Wydrukuj sprawozdanie
      </v-btn>

      <v-btn
        v-if="canDownloadSignedReportFile"
        @click="navigateToFinalReportSigned"
      >
        Pobierz podpisane sprawozdanie
      </v-btn>

      <v-btn
        class="error"
        v-if="canDeleteSignedReportFile"
        @click="deleteSignedReportFile"
      >
        Usuń podpisane sprawozdanie
      </v-btn>

      <project-report-approval-dialog
        :dialog.sync="reportApprovalOpen"
        v-on:approve-report="approveReport"
      >
      </project-report-approval-dialog>

      <egranty-input-field-popup
        title="Oddawanie sprawozdania do poprawek"
        :is-open="correctionCauseOpen"
        field-type="LONG_TEXT"
        label="Powód oddania do poprawek"
        additional-info="Uwaga! Jeżeli podpisany plik sprawozdania końcowego został załączony do projektu, to zostanie on usunięty."
        save-button-label="Wyślij"
        @save="correctReport"
        @cancel="correctionCauseOpen=false"
      >
      </egranty-input-field-popup>

      <v-card v-show="reportVisible">
        <!-- v-show, ponieważ ma być zrenderowane, aby wyeksportować koszty -->
        <final-report-component
          :key="componentKey"
          ref="reportComponent"
          :project-id="projectId"
          :readonly="true"
        />
      </v-card>
    </div>
    <div v-if="loaded && !reportGenerated">
      <h3 class="ml-4 mt-4">
        Żeby sprawozdanie końcowe zostało wygenerowane, musi zostać podpisana
        umowa
      </h3>
    </div>
    <v-container fluid v-if="!reportVisible">
      <v-layout><h3>Uwagi</h3></v-layout>
      <v-layout>
        <v-flex>
          <v-textarea
            outline
            auto-grow
            v-model="finalReportRemarks"
            browser-autocomplete="off"
            aria-label="final report remarks"
          ></v-textarea>
        </v-flex>
      </v-layout>
      <v-btn primary @click="saveFinalReportRemarks">Zapisz</v-btn>
    </v-container>
			<CompareFinalReportDialog
				:last-version="reportMetadata ? reportMetadata.history : null"
        :dialog="showFinalReportDialogComparison"
        @hideReportDialogComparison="hideReportDialogComparison"
        :projectId="projectId"
        />
  </div>
</template>

<script>
import { Project } from "@/api/generated/Project";
import { FinalReport } from "@/api/generated/FinalReport";
import FinalReportComponent from "../finalReport/FinalReportComponent";
import EgrantyInputFieldPopup from "../../../genericComponents/EgrantyInputFieldPopup";
import ProjectReportApprovalDialog from "./components/ProjectReportApprovalDialog";
import { Files } from "@/utils/Files";
import CompareFinalReportDialog from "./compareComponents/CompareFinalReportDialog";

export default {
  name: "ProjectFinalReportView",

  components: {
    ProjectReportApprovalDialog,
    EgrantyInputFieldPopup,
    FinalReportComponent,
    CompareFinalReportDialog,
  },

  mixins: [Files],

  data() {
    return {
      correctionCauseOpen: false,
      reportApprovalOpen: false,
      reportMetadata: null,
      projectStatus: null,
      reportVisible: false,
      componentKey: false,
      projectId: this.$route.params.projectId,
      finalReportRemarks: "",
      showFinalReportDialogComparison: false
    };
  },

  computed: {

    canDeleteSignedReportFile(){
      return this.reportMetadata && this.reportMetadata.canDownloadSignedReportFile;
    },

    canDownloadSignedReportFile() {
      return this.reportMetadata && this.reportMetadata.canDownloadSignedReportFile;
    },

    reportGenerated() {
      return this.loaded && (this.projectStatus === 'AGREEMENT_SIGNED' || this.projectStatus === 'FINISHED');
    },

    currentReportStatus() {
      if (!this.loaded) {
        return "";
      }
      return this.reportMetadata.status;
    },

    exportAvailable() {
      return this.loaded && this.reportMetadata.financialDocumentsIncluded;
    },

    canSeeComparison() {
      return this.reportMetadata
        && (this.reportMetadata.correctionsApplied || (this.currentReportStatus === 'CHANGES_SENT' && this.reportMetadata.history > 1))
        && (this.currentReportStatus === 'SENT' || this.currentReportStatus === 'CHANGES_SENT' || this.currentReportStatus === 'APPROVED');
    },

    canApprove() {
      return this.currentReportStatus === 'SENT'
        || this.currentReportStatus === 'CHANGES_SENT';
    },

    canUndoApproval() {
      return this.currentReportStatus === 'APPROVED';
    },

    canViewFinancialDocuments() {
      return this.loaded && this.reportMetadata.financialDocumentsIncluded
        && (this.currentReportStatus === 'APPROVED'
          || this.currentReportStatus === 'SENT'
          || this.currentReportStatus === 'CHANGES_SENT');
    },

    canCorrect() {
      return this.currentReportStatus === 'SENT'
        || this.currentReportStatus === 'CHANGES_SENT';
    },

    canCancelCorrections() {
      return this.currentReportStatus === 'REQUESTED_CHANGES';
    },

    reportStatusMessage() {
      if (!this.loaded) {
        return "";
      }
      switch (this.currentReportStatus) {
        case 'NEW':
          return 'Sprawozdanie niezłożone';
        case 'APPROVED':
          return 'Sprawozdanie zatwierdzone';
        case "SENT":
          return 'Sprawozdanie wysłane';
        case 'REQUESTED_CHANGES':
          return 'Sprawozdanie oczekuje na poprawki';
        case 'CHANGES_SENT':
          return 'Sprawozdanie poprawione';
        case 'POST_CORRECTION_MAIN':
          return 'Sprawozdanie oczekuje na akceptację';
      }
      return "";
    },

    loaded() {
      return this.projectStatus !== null && this.reportMetadata !== null;
    },

    canEditReport() {
      return this.loaded &&
        (this.projectStatus === 'AGREEMENT_SIGNED');
    },
  },

  methods: {
    deleteSignedReportFile() {
      this.$confirm("Czy na pewno chcesz usunąć plik z podpisanym sprawozdaniem?", {
        color: "blue",
        icon: "warning",
        title: "Usunięcie pliku z podpisanym sprawozdaniem",
        width: 600,
      })
        .then(response => {
          if (response) {
            Project.deleteSignedReportFile(this, this.projectId, successResponse => {
              this.$messagesBus.$emit("success", "Plik z podpisanym sprawozdaniem został usunięty", successResponse);
              this.componentKey = !this.componentKey; // used to rerender the component
              this.refresh();
            }, errorResponse => {
              this.$messagesBus.$emit("error", "Nie udało się usunąć pliku z podpisanym sprawozdaniem", errorResponse);
            });
          }
        });
    },
    async navigateToFinalReportSigned() {
      await this.downloadFileAsBlob(this, this.getSignedProjectFile(this.projectId), this.reportMetadata.signedReportFileName);
    },

    cancelCorrections() {
      FinalReport.postCancelCorrections(
        this,
        this.projectId,
        () => {
          this.$messagesBus.$emit("success", "Cofnięto oddanie sprawozdania do poprawek");
          this.componentKey = !this.componentKey;
          this.refresh();
        },
        errorResponse => {
          this.$messagesBus.$emit("error", "Nie udało się cofnąć oddania sprawozdania do poprawek", errorResponse);
        }
      );
    },

    openApprovalDialog() {
      this.reportApprovalOpen = true;
    },

    openCorrectDialog() {
      this.correctionCauseOpen = true;
    },

    undoApproval() {
      this.$confirm("Czy na pewno chcesz cofnąć zatwierdzenie sprawozdania? Cofnięcie spowoduje usunięcie podanych kwot.", {
        color: "blue",
        icon: "warning",
        title: "Cofnięcie zatwierdzenia sprawozdania",
        width: 600,
      })
        .then(response => {
          if (response) {
            FinalReport.putUndoApproval(this, this.projectId, successResponse => {
              this.$messagesBus.$emit("success", "Zatwierdzenie sprawozdania zostało cofnięte", successResponse);
              this.componentKey = !this.componentKey; // used to rerender the component
              this.refresh();
            }, errorResponse => {
              this.$messagesBus.$emit("error", "Nie udało się cofnąć zatwierdzenia sprawozdania", errorResponse);
            });
          }
        });
    },

    approveReport(data) {
      FinalReport.postApprove(
        this,
        this.projectId,
        data,
        () => {
          this.reportApprovalOpen = false;
          this.componentKey = !this.componentKey; // used to rerender the component
          this.refresh();
        },
        errorResponse => {
          if(errorResponse && errorResponse.message){
            this.$messagesBus.$emit("error", errorResponse.message, errorResponse);
          }else{
            this.$messagesBus.$emit("error", errorResponse.errors[0], errorResponse);
          }
        }
      );
    },

    hideReport() {
      this.reportVisible = false;
    },

    showReport() {
      this.componentKey = !this.componentKey;
      this.reportVisible = true;
    },

    showReportComparison() {
      this.showFinalReportDialogComparison = true;
    },
    hideReportDialogComparison(){
      this.showFinalReportDialogComparison = false;
    },

    correctReport(cause) {
      this.correctionCauseOpen = false;
      FinalReport.postRequestCorrections(
        this,
        this.projectId,
        cause,
        () => {
          this.$messagesBus.$emit("success", "Sprawozdanie zostało oddane do poprawek");
          this.componentKey = !this.componentKey;
          this.refresh();
        },
        errorResponse => {
          this.$messagesBus.$emit("error", "Nie udało się oddać sprawozdania do poprawek", errorResponse);
        }
      );
    },

    exportProjectCosts() {
      this.$refs.reportComponent.exportCosts();
    },

    openReportView() {
      this.$router.openInNewTab('/view/project/' + this.projectId + '/final-report');
    },

    refresh() {
      Project.getStatus(
        this,
        this.projectId,
        response => this.projectStatus = response.projectStatus
      );
      FinalReport.getCurrentState(
        this,
        this.projectId,
        response => this.reportMetadata = response
      );
      Project.getFinalReportRemarks(this,
        this.$route.params.projectId,
        response => this.finalReportRemarks = response.remarks);
    },

    saveFinalReportRemarks() {
      Project.putFinalReportRemarks(this, this.$route.params.projectId, { body: this.finalReportRemarks });
    },

    viewFinancialDocuments() {
      this.$router.push("/app/project/" + this.$route.params.projectId + "/invoices");
    },

    hasProjectPanelSupervisorModificationAccess(){
				return this.$store.state.projectPanelPermissions.hasProjectPanelSupervisorModificationAccess;
			}
  },

  mounted() {
    this.refresh();
  }
};
</script>

<style scoped>
</style>
